import React, { memo, useMemo, useRef, useState } from "react";
import { TrendingDown, TrendingUp, Waves } from "lucide-react";
import { clamp, EmptyData, fmtCompact, fmtPrice, OiArrow, signed } from "./shared";

/*
 * OPTION CHAIN HEATMAP — ATM ± N strikes: premium + change, delta/IV, open
 * interest with today's change, buildup in plain words, ceiling/floor walls.
 * Context for a buyer (where the big positions sit, what the ATM costs); in
 * this system's tests the chain did not predict direction on its own.
 */
const StrikeHeatmap = memo(function StrikeHeatmap({ data }) {
  // Left possibly-undefined on purpose: a `|| []` default here is a fresh
  // array every render and would defeat the useMemo below.
  const chain = data?.chain;
  const atm = Number(data?.atm || 0);
  // Persist last-known OI changes so a momentary zero/empty tick never blanks
  // the display — past changed data stays on screen until a new change arrives.
  const persist = useRef({ ce: null, pe: null, pcr: null, strikes: {} });
  const [span, setSpan] = useState(3);   // strikes shown each side of ATM
  const persistChg = (kind, val) => {
    const n = Number(val);
    if (val != null && !Number.isNaN(n) && n !== 0) persist.current[kind] = n;
    return persist.current[kind];
  };
  const grouped = useMemo(() => {
    const map = new Map();
    (chain || []).forEach((row) => {
      const strike = Number(row.strike);
      const pair = map.get(strike) || { strike };
      pair[row.option_type?.toLowerCase()] = row;
      map.set(strike, pair);
    });
    const all = [...map.values()].sort((a, b) => a.strike - b.strike);
    const atmIndex = Math.max(0, all.findIndex((r) => r.strike === atm));
    return all.slice(Math.max(0, atmIndex - span), Math.min(all.length, atmIndex + span + 1));
  }, [chain, atm, span]);
  // Where today's new selling is concentrated, over the WHOLE chain (not only the rows shown).
  const freshest = useMemo(() => {
    const top = (type) => (chain || [])
      .filter((r) => r.option_type === type && Number(r.day_oi_change || 0) > 0)
      .sort((a, b) => Number(b.day_oi_change) - Number(a.day_oi_change))[0] || null;
    return { ce: top("CE"), pe: top("PE") };
  }, [chain]);

  const maxDayOi = Math.max(1, ...grouped.flatMap((row) => [Math.abs(row.ce?.day_oi_change || 0), Math.abs(row.pe?.day_oi_change || 0)]));
  const maxVolume = Math.max(1, ...grouped.flatMap((row) => [row.ce?.volume || 0, row.pe?.volume || 0]));
  const maxOi = Math.max(1, ...grouped.flatMap((row) => [Number(row.ce?.oi || 0), Number(row.pe?.oi || 0)]));
  const spot = Number(data?.spot || 0);
  const maxPain = Number(data?.max_pain || 0);
  const support = Number(data?.support || 0);
  const resistance = Number(data?.resistance || 0);
  if (!grouped.length) return <EmptyData />;

  // Net directional read from the chain: who is winning the OI battle.
  const oiScore = Number(data?.score ?? 50);
  let putWrite = 0, callWrite = 0, callBuild = 0, putBuild = 0;
  grouped.forEach((r) => {
    if (r.ce?.buildup === "SHORT BUILDUP") callWrite += 1;   // call writers → resistance (bearish)
    if (r.pe?.buildup === "SHORT BUILDUP") putWrite += 1;    // put writers → support (bullish)
    if (r.ce?.buildup === "LONG BUILDUP") callBuild += 1;    // call buyers (bullish)
    if (r.pe?.buildup === "LONG BUILDUP") putBuild += 1;     // put buyers (bearish)
  });
  const up = oiScore >= 58 || (putWrite + callBuild) - (callWrite + putBuild) >= 2;
  const down = !up && (oiScore <= 42 || (callWrite + putBuild) - (putWrite + callBuild) >= 2);
  const reasonParts = [];
  if (putWrite > callWrite) reasonParts.push("put writers defending support");
  else if (callWrite > putWrite) reasonParts.push("call writers capping upside");
  if (callBuild > putBuild) reasonParts.push("fresh call buying");
  else if (putBuild > callBuild) reasonParts.push("fresh put buying");
  const biasReason = reasonParts.join(" · ") || "balanced OI flows — no clear pressure";
  const bias = up
    ? { text: "text-emerald-300", bg: "bg-emerald-500/[.10]", border: "border-emerald-500/30", Icon: TrendingUp, label: "LIKELY UP", note: "OI flow favours upside" }
    : down
      ? { text: "text-rose-300", bg: "bg-rose-500/[.10]", border: "border-rose-500/30", Icon: TrendingDown, label: "LIKELY DOWN", note: "OI flow favours downside" }
      : { text: "text-amber-300", bg: "bg-amber-500/[.08]", border: "border-amber-500/30", Icon: Waves, label: "RANGE-BOUND", note: "no decisive OI pressure" };
  const BiasIcon = bias.Icon;

  // Plain words for the four OI states; the textbook term stays in the tooltip.
  const buildupLabel = (row, type) => {
    const b = row?.buildup;
    if (!b || b === "NEUTRAL") return "no change";
    if (b === "SHORT BUILDUP") return type === "CE" ? "Sellers adding · cap" : "Sellers adding · floor";
    if (b === "LONG BUILDUP") return "Buyers adding";
    if (b === "SHORT COVERING") return "Sellers leaving";
    if (b === "LONG UNWINDING") return "Buyers leaving";
    return b;
  };
  const buildupHint = (row, type) => ({
    "SHORT BUILDUP": type === "CE"
      ? "Short buildup: call sellers are adding positions here — they expect price to stay BELOW this strike"
      : "Short buildup: put sellers are adding positions here — they expect price to stay ABOVE this strike",
    "LONG BUILDUP": "Long buildup: open interest and premium both rising — buyers are adding",
    "SHORT COVERING": "Short covering: sellers are closing positions — the wall here is weakening",
    "LONG UNWINDING": "Long unwinding: buyers are closing positions",
  }[row?.buildup] || "No clear change in positions");

  const premiumCell = (row, type) => {
    if (!row) return <div className="py-2 text-center text-slate-700">—</div>;
    const change = Number(row.premium_change_pct || 0);
    const good = change > 0;
    const volumeWidth = clamp((Number(row.volume || 0) / maxVolume) * 100);
    return (
      <div className={`relative overflow-hidden px-2 py-2 text-center ${good ? (type === "CE" ? "bg-emerald-500/10" : "bg-rose-500/10") : "bg-slate-950/20"}`}>
        <div className={`absolute bottom-0 left-0 h-0.5 ${type === "CE" ? "bg-emerald-400/70" : "bg-rose-400/70"}`} style={{ width: `${volumeWidth}%` }} />
        <p className={`text-[11px] font-black tabular-nums ${good ? (type === "CE" ? "text-emerald-300" : "text-rose-300") : "text-slate-400"}`}>{signed(change)}</p>
        <p className="text-[10px] text-slate-600">₹{fmtPrice(row.last_price, 1)} · <span className={Number(row.vwap_distance_pct || 0) >= 0 ? "text-cyan-500" : "text-amber-500"}>V{Number(row.vwap_distance_pct || 0) >= 0 ? "↑" : "↓"}</span></p>
        <p className="text-[10px] tabular-nums text-slate-500" title="delta · implied volatility">Δ {row.delta == null || Number.isNaN(Number(row.delta)) ? "—" : fmtPrice(Math.abs(Number(row.delta)), 2)} · IV {row.iv == null ? "—" : `${fmtPrice(row.iv, 1)}%`}</p>
      </div>
    );
  };

  const oiCell = (row, type) => {
    if (!row) return <div className="py-2 text-center text-slate-700">—</div>;
    const oi = Number(row.oi || 0);
    const dayDelta = Number(row.day_oi_change || 0);      // heatmap tint + buildup color
    const alpha = 0.06 + (Math.abs(dayDelta) / maxDayOi) * 0.18;
    const isWrite = row.buildup === "SHORT BUILDUP";
    // Displayed change = LAST-TICK OI delta, persisted per strike so a zero/
    // empty tick never blanks it (shows the most recent actual change).
    const key = `${type}-${row.strike}`;
    let tickDelta = Number(row.oi_change || 0);
    if (tickDelta !== 0) persist.current.strikes[key] = tickDelta;
    else if (persist.current.strikes[key] != null) tickDelta = persist.current.strikes[key];
    const up = tickDelta > 0;
    return (
      <div className="px-2 py-2 text-center" style={{ background: dayDelta >= 0 ? `rgba(245,158,11,${alpha})` : `rgba(56,189,248,${alpha})` }}>
        <p className="text-[11px] font-black tabular-nums text-slate-100">{fmtCompact(oi)}</p>
        <p className={`text-[10px] font-bold tabular-nums ${tickDelta === 0 ? "text-slate-600" : up ? "text-emerald-400" : "text-rose-400"}`}
           title="OI change — last tick">
          {tickDelta === 0 ? "▬" : up ? "▲" : "▼"} {fmtCompact(Math.abs(tickDelta))}
        </p>
        <p title={buildupHint(row, type)} className={`text-[10px] font-bold ${isWrite ? (type === "CE" ? "text-rose-400" : "text-emerald-400") : dayDelta >= 0 ? "text-amber-300" : "text-cyan-300"}`}>{buildupLabel(row, type)}</p>
        {/* size of the position at this strike, drawn toward the strike column */}
        <div className={`mt-1 flex h-1.5 ${type === "CE" ? "justify-end" : "justify-start"}`} title={`${Number(oi).toLocaleString("en-IN")} open contracts · today ${dayDelta >= 0 ? "+" : "−"}${fmtCompact(Math.abs(dayDelta))}`}>
          <div className={`h-full rounded-full ${type === "CE" ? "bg-rose-400/80" : "bg-emerald-400/80"}`} style={{ width: `${clamp((oi / maxOi) * 100)}%` }} />
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className={`m-3 flex items-center justify-between gap-3 rounded-xl border px-3 py-2.5 ${bias.bg} ${bias.border}`}>
        <div className="flex items-center gap-2.5">
          <BiasIcon className={`h-5 w-5 shrink-0 ${bias.text}`} />
          <div className="min-w-0">
            <p className={`text-sm font-black ${bias.text}`}>Option chain: {bias.label}</p>
            <p className="text-[10px] leading-4 text-slate-400">{biasReason}</p>
          </div>
        </div>
        <div className="text-right"><p className={`text-lg font-black tabular-nums ${bias.text}`}>{oiScore}</p><p className="text-[10px] uppercase tracking-wider text-slate-600">OI score</p></div>
      </div>
      <div className="mx-3 mb-3 grid grid-cols-3 gap-px overflow-hidden rounded-xl border border-slate-800 bg-slate-800 text-center">
        <div className="bg-slate-950/60 px-2 py-2">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Total Call OI</p>
          <p className="text-sm font-black tabular-nums text-rose-300" title={data?.ce_oi != null ? Number(data.ce_oi).toLocaleString("en-IN") : ""}>{data?.ce_oi != null ? fmtCompact(data.ce_oi) : "—"}</p>
          <OiArrow value={persistChg("ce", data?.ce_oi_change)} />
        </div>
        <div className="bg-slate-950/60 px-2 py-2">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">PCR (OI)</p>
          <p className="text-sm font-black tabular-nums text-amber-300">{data?.pcr != null ? Number(data.pcr).toFixed(2) : "—"}</p>
          <OiArrow value={persistChg("pcr", data?.pcr_change?.["5m"])} fmt={(v) => Number(v).toFixed(2)} label="/5m" />
          <p className="text-[10px] text-slate-600">{data?.pcr_scope === "FULL_EXPIRY" ? "Full expiry" : "Near ATM"}</p>
        </div>
        <div className="bg-slate-950/60 px-2 py-2">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Total Put OI</p>
          <p className="text-sm font-black tabular-nums text-emerald-300" title={data?.pe_oi != null ? Number(data.pe_oi).toLocaleString("en-IN") : ""}>{data?.pe_oi != null ? fmtCompact(data.pe_oi) : "—"}</p>
          <OiArrow value={persistChg("pe", data?.pe_oi_change)} />
        </div>
      </div>
      <div data-testid="chain-key-levels" className="mx-3 mb-3 grid grid-cols-1 gap-2 text-sm sm:grid-cols-3">
        <div className="rounded-xl border border-rose-500/25 bg-rose-500/[.06] px-3 py-2">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Ceiling (most call sellers)</p>
          <p className="text-lg font-black tabular-nums text-rose-300">{resistance ? fmtPrice(resistance) : "—"}</p>
          <p className="text-[12px] text-slate-400">{resistance && spot ? (spot < resistance ? `${fmtPrice(resistance - spot)} pts above the price` : `price is ${fmtPrice(spot - resistance)} pts ABOVE it — broken`) : "no level yet"}</p>
        </div>
        <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/[.06] px-3 py-2">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Floor (most put sellers)</p>
          <p className="text-lg font-black tabular-nums text-emerald-300">{support ? fmtPrice(support) : "—"}</p>
          <p className="text-[12px] text-slate-400">{support && spot ? (spot > support ? `${fmtPrice(spot - support)} pts below the price` : `price is ${fmtPrice(support - spot)} pts BELOW it — broken`) : "no level yet"}</p>
        </div>
        <div className="rounded-xl border border-line bg-surface-2 px-3 py-2">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Today's new selling</p>
          <p className="text-[13px] leading-5 text-slate-300">Calls: <b className="text-rose-300">{freshest.ce ? `${fmtPrice(freshest.ce.strike)} (+${fmtCompact(freshest.ce.day_oi_change)})` : "—"}</b></p>
          <p className="text-[13px] leading-5 text-slate-300">Puts: <b className="text-emerald-300">{freshest.pe ? `${fmtPrice(freshest.pe.strike)} (+${fmtCompact(freshest.pe.day_oi_change)})` : "—"}</b></p>
          {maxPain > 0 && <p className="text-[12px] text-slate-500" title="The strike where option buyers lose the most at expiry">Max pain {fmtPrice(maxPain)}</p>}
        </div>
      </div>
      <div className="mx-3 mb-2 flex items-center justify-between gap-2">
        <p className="text-[12px] text-slate-400">Bars show how many contracts are open at each strike. Long red bar = ceiling, long green bar = floor.</p>
        <div className="flex shrink-0 rounded-lg bg-slate-950 p-0.5" role="group" aria-label="Strikes shown">
          {[3, 6, 10].map((k) => (
            <button key={k} type="button" onClick={() => setSpan(k)} aria-pressed={span === k}
              className={`rounded-md px-2.5 py-1 text-[11px] font-black ${span === k ? "bg-cyan-400 text-slate-950" : "text-slate-500"}`}>±{k}</button>
          ))}
        </div>
      </div>
      <div className="overflow-x-auto">
      <div className="min-w-[560px]">
        <div className="grid border-b border-slate-800 bg-slate-950/50 text-center text-[10px] font-bold uppercase tracking-wider text-slate-600" style={{ gridTemplateColumns: "1fr 1fr 84px 1fr 1fr" }}>
          <div className="px-2 py-2 text-emerald-500">Call price · change</div><div className="px-2 py-2">Call positions</div><div className="px-2 py-2">Strike</div><div className="px-2 py-2">Put positions</div><div className="px-2 py-2 text-rose-500">Put price · change</div>
        </div>
        {grouped.map((row, idx) => {
          const isAtm = row.strike === atm;
          // the price sits between the previous strike and this one
          const spotHere = spot > 0 && idx > 0 && grouped[idx - 1].strike <= spot && spot < row.strike;
          const ceOi = Number(row.ce?.oi || 0), peOi = Number(row.pe?.oi || 0);
          const strikePcr = ceOi > 0 ? peOi / ceOi : null;
          const isSupport = support && row.strike === support;
          const isResistance = resistance && row.strike === resistance;
          const rowTint = isResistance ? "bg-rose-500/[.06]" : isSupport ? "bg-emerald-500/[.06]" : isAtm ? "bg-cyan-500/[.06]" : "";
          return (
            <React.Fragment key={row.strike}>
            {spotHere && (
              <div data-testid="chain-spot-row" className="flex items-center gap-2 border-b border-cyan-400/40 bg-cyan-400/[.08] px-3 py-1 text-[11px] font-black text-cyan-300">
                <span className="h-px flex-1 bg-cyan-400/50" />PRICE NOW {fmtPrice(spot, 1)}<span className="h-px flex-1 bg-cyan-400/50" />
              </div>
            )}
            <div className={`grid border-b border-slate-800/70 last:border-0 ${rowTint}`} style={{ gridTemplateColumns: "1fr 1fr 84px 1fr 1fr" }}>
              {premiumCell(row.ce, "CE")}{oiCell(row.ce, "CE")}
              <div className="flex flex-col items-center justify-center border-x border-slate-800 px-1 py-1.5">
                <p className={`text-sm font-black tabular-nums ${isAtm ? "text-cyan-300" : "text-hi"}`}>{fmtPrice(row.strike)}</p>
                {isResistance ? <span className="mt-0.5 rounded bg-rose-500/20 px-1 text-[10px] font-black uppercase tracking-wider text-rose-300">RESIST</span>
                  : isSupport ? <span className="mt-0.5 rounded bg-emerald-500/20 px-1 text-[10px] font-black uppercase tracking-wider text-emerald-300">SUPPORT</span>
                  : <p className="text-[10px] font-bold uppercase tracking-wider text-slate-600">{isAtm ? "ATM" : row.strike > atm ? "above" : "below"}</p>}
                {strikePcr != null && (
                  <p title="Put contracts ÷ call contracts at this strike. Above 1 = more put sellers (floor); below 1 = more call sellers (ceiling)."
                    className={`text-[10px] font-bold tabular-nums ${strikePcr >= 1.2 ? "text-emerald-400" : strikePcr <= 0.8 ? "text-rose-400" : "text-slate-500"}`}>
                    {strikePcr >= 1.2 ? "floor" : strikePcr <= 0.8 ? "ceiling" : "even"} {strikePcr.toFixed(1)}
                  </p>
                )}
              </div>
              {oiCell(row.pe, "PE")}{premiumCell(row.pe, "PE")}
            </div>
            </React.Fragment>
          );
        })}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-800 bg-slate-950/30 px-3 py-2 text-[10px] text-slate-600">
        <span><b className="text-rose-400">Sellers adding · cap</b> = call sellers expect price to stay below · <b className="text-emerald-400">Sellers adding · floor</b> = put sellers expect price to stay above · Amber cell = positions growing today · Blue = shrinking</span>
        <span>The chain shows where big positions sit. In this system's tests it did not predict direction on its own.</span>
      </div>
      </div>
    </div>
  );
});

export default StrikeHeatmap;
